import { useShadow, useShadowStyle } from "../../util/wcUtil.ts"
import styleText from './shared-modal-styles.css?raw';
import { applyLocalizedText, applyLabelVars } from '../base.ts';
import { closePanel, onPanelButtons } from './modalPanel.ts';

export interface ParamateErrorModalContent {
  titleKey: string;
  defaultTitle: string;
  messageKey: string;
  defaultMessage: string;
  sessionId?: string | null;
}

/**
 * Dedicated modal for classified Paramate SDK errors (network/trcad
 * timeout-or-memory/unsupported-file/etc — see paramateErrorInterceptor.ts).
 * Distinct from <as-error-info>, which stays the generic panel for genuinely
 * uncaught JS errors (window.onerror / unhandledrejection).
 *
 * Panels opened via as-app's openModal() are cached/reused by id, so content
 * is set post-construction via setContent() rather than fixed markup.
 */
export class AsModalParamateError extends HTMLElement {
  static readonly tag = 'as-modal-paramate-error' as const;

  get html() {
    return /*html*/`
      <div class="panel-content">
        <as-text id="modal-title" class="modal-title"></as-text>
        <as-text id="modal-desc" class="modal-desc"></as-text>
        <as-text id="session-id-line" class="modal-desc" style="margin-top: 1rem; opacity: 0.7; font-size: 0.85em;"></as-text>
        <div class="row option-buttons">
          <as-text-button name="dismiss-paramate-error" variant="primary" dict-key="modals.paramate-error.ok">OK</as-text-button>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    useShadow(this, this.html)
    useShadowStyle(this, styleText, './shared-modal-styles.css')
    // OK dismisses and nothing else, so the panel handles it without troubling
    // the app.
    onPanelButtons(this, { 'dismiss-paramate-error': () => closePanel(this) });
  }

  setContent(content: ParamateErrorModalContent) {
    const titleEl = this.shadowRoot?.querySelector('#modal-title');
    const descEl = this.shadowRoot?.querySelector('#modal-desc');
    const sessionEl = this.shadowRoot?.querySelector('#session-id-line');

    if (titleEl) {
      applyLocalizedText(titleEl as HTMLElement, content.titleKey, content.defaultTitle);
    }
    if (descEl) {
      applyLocalizedText(descEl as HTMLElement, content.messageKey, content.defaultMessage);
    }
    if (sessionEl) {
      const el = sessionEl as HTMLElement;
      el.hidden = !content.sessionId;
      if (content.sessionId) {
        applyLabelVars(el, [content.sessionId]);
        applyLocalizedText(el, 'modals.paramate-error.session-id', 'Session ID: {0}');
      }
    }
  }
}

if (!customElements.get(AsModalParamateError.tag)) {
  customElements.define(AsModalParamateError.tag, AsModalParamateError);
} else {
  console.warn('as-modal-paramate-error already defined');
}
